import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Play, Sparkles, Wand2, Mic, Palette, Music,
  Film, Link2, ScanLine, Volume2, VolumeX, Mail, Building2, Car,
  UtensilsCrossed, Hammer, Camera, Store, Layers, Plane, Users, Package,
} from "lucide-react";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const EMAIL = "abercallabs@gmail.com";
const MAILTO = `mailto:${EMAIL}?subject=${encodeURIComponent("Listing to Video — Request")}`;

const HERO_BG = "https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FALLBACK = [
  { img: "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", tag: "Real Estate", cap: "Modern Villa · $2.4M" },
  { img: "https://images.pexels.com/photos/20131971/pexels-photo-20131971.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=700", tag: "Dealership", cap: "Certified · Full walkaround" },
  { img: "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", tag: "Restaurant", cap: "Chef's Tasting Menu" },
  { img: "https://images.unsplash.com/photo-1497316730643-415fac54a2af?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", tag: "Photographer", cap: "Golden-hour portfolio" },
  { img: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", tag: "Trades", cap: "Same-day service" },
  { img: "https://images.unsplash.com/photo-1590486803833-1c5dc8ddd4c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", tag: "Small Business", cap: "Creators & studios" },
];

const STEPS = [
  { n: "01", icon: Link2, t: "Paste a link or a topic", d: "Drop any listing URL — a home, a car, a menu, a portfolio — or just describe the business. We pull the photos and the facts automatically." },
  { n: "02", icon: Wand2, t: "The AI directs it", d: "It writes the script and on-screen cards, generates visuals, records an ElevenLabs voiceover and scores the music — all on your brand." },
  { n: "03", icon: Film, t: "Export your reel", d: "Download a polished, ready-to-post vertical film — or square and wide too. From link to launch in minutes." },
];

const NICHES = [
  { icon: Building2, label: "Real Estate" },
  { icon: Car, label: "Dealerships" },
  { icon: UtensilsCrossed, label: "Restaurants" },
  { icon: Hammer, label: "Trades" },
  { icon: Camera, label: "Photographers" },
  { icon: Store, label: "Small Business" },
];

/* ---------- helpers ---------- */
const Noise = () => (
  <div
    aria-hidden
    className="pointer-events-none fixed inset-0 z-[100] opacity-[0.04] mix-blend-overlay"
    style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
  />
);

const Reveal = ({ children, className = "", delay = 0, y = 40 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const GlowButton = ({ to, href, children, testid, className = "" }) => {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-7 py-3.5 font-['DM_Sans'] text-[15px] font-bold text-[#0a0500] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_35px_rgba(245,166,35,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505] ${className}`;
  const inner = (<>{children}<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>);
  if (to) return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
  return <a href={href} data-testid={testid} className={cls}>{inner}</a>;
};

const GhostButton = ({ href, children, testid }) => (
  <a href={href} data-testid={testid}
    className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 font-['DM_Sans'] text-[15px] font-medium text-zinc-200 backdrop-blur-xl transition-[transform,border-color,background-color] duration-300 hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10">
    {children}
  </a>
);

/* ---------- navigation ---------- */
const Nav = () => (
  <nav className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-12">
      <a href="#top" data-testid="nav-logo" className="font-['Outfit'] text-xl font-black tracking-tight text-white">
        Listing<span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">to</span>Video
      </a>
      <div className="hidden items-center gap-9 md:flex">
        {[["How it works", "#how"], ["Showcase", "#showcase"], ["Features", "#features"]].map(([l, h]) => (
          <a key={h} href={h} className="font-['DM_Sans'] text-sm text-zinc-400 transition-colors duration-200 hover:text-white">{l}</a>
        ))}
      </div>
      <div className="flex items-center gap-3 sm:gap-5">
        <Link to="/login" data-testid="nav-login-link" className="font-['DM_Sans'] text-sm font-medium text-zinc-300 transition-colors duration-200 hover:text-amber-400">
          Studio Login
        </Link>
        <GlowButton to="/login" testid="nav-cta-button" className="!px-5 !py-2.5 !text-sm">Sign in</GlowButton>
      </div>
    </div>
  </nav>
);

/* ---------- hero ---------- */
const Hero = () => (
  <section className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pt-28 text-center">
    <div className="absolute inset-0 -z-10">
      <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505]" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(120% 80% at 50% 0%, transparent 30%, #050505 100%)" }} />
      <div className="absolute left-1/2 top-1/3 h-[420px] w-[620px] -translate-x-1/2 rounded-full bg-amber-500/15 blur-[140px]" />
    </div>

    <Reveal y={20}>
      <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 font-['DM_Sans'] text-xs font-medium tracking-wide text-zinc-300 backdrop-blur-xl">
        <Sparkles className="h-3.5 w-3.5 text-amber-400" /> AI video studio for local business
      </span>
    </Reveal>

    <Reveal delay={0.08}>
      <h1 className="mt-7 max-w-4xl font-['Outfit'] text-5xl font-black leading-[1.03] tracking-tighter text-white sm:text-6xl md:text-7xl">
        Turn a link or a topic into{" "}
        <span className="font-['Playfair_Display'] font-normal italic text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-500">
          cinematic reels &amp; cards
        </span>{" "}
        — in minutes.
      </h1>
    </Reveal>

    <Reveal delay={0.16}>
      <p className="mx-auto mt-6 max-w-xl font-['DM_Sans'] text-base leading-relaxed text-zinc-400 sm:text-lg">
        Not just property listings. Paste any URL — or just a topic — and our AI writes the
        script, generates the visuals, records the narration and cuts a branded, ready-to-post
        video for cars, restaurants, trades, creators and any business.
        <span className="mt-3 block text-sm text-zinc-500">
          Build each one from real listing footage <em className="not-italic text-zinc-400">or</em> from AI-generated cards — same studio, same export.
        </span>
      </p>
    </Reveal>

    <Reveal delay={0.24}>
      <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
        <GlowButton to="/login" testid="hero-cta-button">Sign in to Studio</GlowButton>
        <GhostButton href="#showcase" testid="hero-showcase-button">
          <Play className="h-4 w-4 fill-current" /> View showcase
        </GhostButton>
      </div>
    </Reveal>

    <Reveal delay={0.34}>
      <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-['DM_Sans'] text-xs uppercase tracking-[0.2em] text-zinc-500">
        <span className="flex items-center gap-2"><Mic className="h-3.5 w-3.5 text-amber-400" /> AI narration</span>
        <span className="flex items-center gap-2"><Palette className="h-3.5 w-3.5 text-amber-400" /> On-brand</span>
        <span className="flex items-center gap-2"><ScanLine className="h-3.5 w-3.5 text-amber-400" /> 9:16 · 1:1 · 16:9</span>
      </div>
    </Reveal>
  </section>
);

/* ---------- showcase reel card ---------- */
const ReelCard = ({ reel }) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const isVideo = !!reel.final_media_id;
  const toggle = (e) => {
    e.preventDefault();
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };
  return (
    <div
      data-testid={reel.id ? `showcase-reel-${reel.id}` : "showcase-video-card"}
      className="group relative aspect-[9/16] w-64 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-[#121212] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] transition-transform duration-500 ease-out hover:z-10 hover:scale-[1.04] sm:w-72"
    >
      {isVideo ? (
        <video ref={ref} src={`${BACKEND}/api/media/${reel.final_media_id}`} muted loop playsInline autoPlay preload="metadata"
          className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <img src={reel.img} alt={reel.cap} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/20" />

      {reel.tag && (
        <div className="absolute left-3 top-3 rounded-full border border-white/15 bg-black/40 px-3 py-1 font-['DM_Sans'] text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-md">
          {reel.tag}
        </div>
      )}
      {isVideo && (
        <button onClick={toggle} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white backdrop-blur-md transition-colors hover:bg-black/70">
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      )}
      {!isVideo && (
        <div className="absolute right-3 top-3 rounded-full border border-white/15 bg-black/40 px-2.5 py-1 font-['DM_Sans'] text-[9px] font-semibold uppercase tracking-[0.15em] text-white/80 backdrop-blur-md">
          Sample
        </div>
      )}
      <div className="pointer-events-none absolute bottom-4 left-4 right-4">
        <p className="line-clamp-2 font-['Outfit'] text-lg font-bold leading-tight text-white">{reel.title || reel.cap}</p>
        <p className="mt-1 font-['DM_Sans'] text-[10px] uppercase tracking-[0.2em] text-amber-400/80">Listing to Video</p>
      </div>
    </div>
  );
};

/* ---------- showcase (infinite marquee) ---------- */
const Showcase = ({ reels }) => {
  const hasReels = reels && reels.length > 0;
  const items = hasReels ? reels.slice(0, 8) : FALLBACK;
  const loop = [...items, ...items];
  const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <section id="showcase" className="relative overflow-hidden py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12">
        <Reveal>
          <p className="font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.25em] text-amber-400">The output</p>
          <h2 className="mt-4 max-w-3xl font-['Outfit'] text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
            {hasReels ? "Real reels, made from a single link." : "Scroll-stopping reels for every kind of business."}
          </h2>
          <p className="mt-4 max-w-xl font-['DM_Sans'] text-base leading-relaxed text-zinc-400">
            {hasReels ? "Tap the speaker on any reel to hear the AI narration." : "Every one of these could be your listing — branded, narrated and cut for you."}
          </p>
        </Reveal>
      </div>

      <div className="relative mt-14" style={{ perspective: 1200 }}>
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-[#050505] to-transparent sm:w-40" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-[#050505] to-transparent sm:w-40" />
        <motion.div
          className="flex w-max gap-6 px-6"
          animate={reduce ? {} : { x: ["0%", "-50%"] }}
          transition={{ duration: 55, ease: "linear", repeat: Infinity }}
        >
          {loop.map((r, i) => <ReelCard key={i} reel={r} />)}
        </motion.div>
      </div>
    </section>
  );
};

/* ---------- how it works ---------- */
const HowItWorks = () => (
  <section id="how" className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32">
    <Reveal>
      <p className="font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.25em] text-amber-400">The process</p>
      <h2 className="mt-4 max-w-2xl font-['Outfit'] text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
        Three steps. That's the whole crew.
      </h2>
    </Reveal>
    <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        return (
          <Reveal key={s.n} delay={i * 0.1}>
            <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 transition-[transform,border-color] duration-300 hover:-translate-y-2 hover:border-amber-400/30 sm:p-10">
              <span className="pointer-events-none absolute -right-4 -top-8 font-['Outfit'] text-[9rem] font-black leading-none text-white/[0.04]">{s.n}</span>
              <div className="relative">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400/20 to-orange-500/10 text-amber-400 ring-1 ring-inset ring-amber-400/20">
                  <Icon className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <h3 className="mt-6 font-['Outfit'] text-2xl font-bold text-white">{s.t}</h3>
                <p className="mt-3 font-['DM_Sans'] text-[15px] leading-relaxed text-zinc-400">{s.d}</p>
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

/* ---------- feature bento ---------- */
const BentoCard = ({ className = "", children, delay = 0 }) => (
  <Reveal delay={delay} className={className}>
    <div className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-amber-400/30 sm:p-10">
      {children}
    </div>
  </Reveal>
);

const Waveform = () => (
  <div className="mt-6 flex h-16 items-end gap-1.5">
    {[10, 24, 40, 30, 55, 38, 64, 44, 28, 50, 34, 60, 22, 46, 16, 52, 30, 42].map((h, i) => (
      <motion.span key={i}
        className="w-1.5 rounded-full bg-gradient-to-t from-amber-500/40 to-amber-400"
        initial={{ height: 6 }}
        whileInView={{ height: h }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: i * 0.03, ease: "easeOut" }}
      />
    ))}
  </div>
);

const Features = () => (
  <section id="features" className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32">
    <Reveal>
      <p className="font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.25em] text-amber-400">What's inside</p>
      <h2 className="mt-4 max-w-2xl font-['Outfit'] text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
        Everything a film crew does. None of the wait.
      </h2>
    </Reveal>

    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12">
      <BentoCard className="md:col-span-7" delay={0}>
        <div className="flex items-center gap-3 text-amber-400"><Mic className="h-5 w-5" /><span className="font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.2em]">AI script + voiceover</span></div>
        <h3 className="mt-4 font-['Outfit'] text-2xl font-bold text-white">Words written, then spoken.</h3>
        <p className="mt-2 max-w-md font-['DM_Sans'] text-[15px] leading-relaxed text-zinc-400">
          The AI drafts the headline, the on-screen cards and the narration — then records it with a natural ElevenLabs voice you pick, at the pace you set, pauses and all.
        </p>
        <Waveform />
      </BentoCard>

      <BentoCard className="md:col-span-5" delay={0.06}>
        <div className="flex items-center gap-3 text-amber-400"><Palette className="h-5 w-5" /><span className="font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.2em]">Custom branding</span></div>
        <h3 className="mt-4 font-['Outfit'] text-2xl font-bold text-white">Your logo. Your colors.</h3>
        <p className="mt-2 font-['DM_Sans'] text-[15px] leading-relaxed text-zinc-400">
          Drop in a logo and brand color and every reel gets a branded intro and matched palette automatically.
        </p>
        <div className="mt-6 flex gap-2">
          {["#F5A623", "#FF4B2B", "#0EA5E9", "#22C55E", "#A855F7"].map((c) => (
            <span key={c} className="h-9 w-9 rounded-xl ring-1 ring-inset ring-white/10" style={{ background: c }} />
          ))}
        </div>
      </BentoCard>

      <BentoCard className="md:col-span-5" delay={0.12}>
        <div className="flex items-center gap-3 text-amber-400"><Music className="h-5 w-5" /><span className="font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.2em]">Visuals + music</span></div>
        <h3 className="mt-4 font-['Outfit'] text-2xl font-bold text-white">AI backgrounds & your own track.</h3>
        <p className="mt-2 font-['DM_Sans'] text-[15px] leading-relaxed text-zinc-400">
          Generate fitting background images or drop a video behind any card — then upload your own music to score it.
        </p>
      </BentoCard>

      <BentoCard className="md:col-span-7" delay={0.18}>
        <div className="flex items-center gap-3 text-amber-400"><ScanLine className="h-5 w-5" /><span className="font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.2em]">Multi-format export</span></div>
        <h3 className="mt-4 font-['Outfit'] text-2xl font-bold text-white">One reel, every platform.</h3>
        <p className="mt-2 max-w-md font-['DM_Sans'] text-[15px] leading-relaxed text-zinc-400">
          Export vertical for Reels & TikTok, square for the feed, and wide for YouTube — from the same project.
        </p>
        <div className="mt-7 flex items-end gap-4">
          {[["9:16", "h-24 w-[54px]"], ["1:1", "h-20 w-20"], ["16:9", "h-[54px] w-24"]].map(([r, size]) => (
            <div key={r} className="flex flex-col items-center gap-2">
              <div className={`rounded-lg border border-white/15 bg-gradient-to-br from-amber-400/15 to-orange-500/5 ${size}`} />
              <span className="font-['DM_Sans'] text-xs font-medium text-zinc-500">{r}</span>
            </div>
          ))}
        </div>
      </BentoCard>
    </div>
  </section>
);

/* ---------- beyond reels ---------- */
const TOOLS = [
  { icon: Layers, t: "Cards reels", d: "Build narrated slide/card videos with AI-written text and AI-generated backgrounds — perfect when you have no footage, just a message." },
  { icon: Plane, t: "Flythrough drone effect", d: "Turn a set of photos into a smooth, cinematic drone-style flythrough with auto-routed camera moves between shots." },
  { icon: Users, t: "On-location AI host", d: "Add an AI presenter composited on-camera to introduce the property, product or business like a real host." },
  { icon: Package, t: "Import & export projects", d: "Move whole projects between accounts and environments with a portable .reelpack — finished video included." },
];

const MoreTools = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32">
    <Reveal>
      <p className="font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.25em] text-amber-400">More than listings</p>
      <h2 className="mt-4 max-w-3xl font-['Outfit'] text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
        A whole toolkit — not just one trick.
      </h2>
      <p className="mt-4 max-w-xl font-['DM_Sans'] text-base leading-relaxed text-zinc-400">
        It started with property listings. Now it makes reels for any business, from a link or a topic — with extra ways to shoot.
      </p>
    </Reveal>
    <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2">
      {TOOLS.map((t, i) => {
        const Icon = t.icon;
        return (
          <Reveal key={t.t} delay={i * 0.08}>
            <div className="group flex h-full items-start gap-5 rounded-3xl border border-white/10 bg-white/5 p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-amber-400/30">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400/20 to-orange-500/10 text-amber-400 ring-1 ring-inset ring-amber-400/20">
                <Icon className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="font-['Outfit'] text-xl font-bold text-white">{t.t}</h3>
                <p className="mt-2 font-['DM_Sans'] text-[15px] leading-relaxed text-zinc-400">{t.d}</p>
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

/* ---------- who it's for ---------- */
const WhoFor = () => (
  <section className="mx-auto max-w-7xl px-6 pb-8 sm:px-12">
    <Reveal>
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 sm:p-12">
        <p className="text-center font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Built for</p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
          {NICHES.map((n) => {
            const Icon = n.icon;
            return (
              <span key={n.label} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#0d0d0d] px-5 py-2.5 font-['DM_Sans'] text-sm font-medium text-zinc-300 transition-colors duration-200 hover:border-amber-400/40 hover:text-white">
                <Icon className="h-4 w-4 text-amber-400" strokeWidth={1.75} /> {n.label}
              </span>
            );
          })}
        </div>
      </div>
    </Reveal>
  </section>
);

/* ---------- CTA + footer ---------- */
const CTA = () => (
  <section className="relative mx-auto max-w-7xl overflow-hidden px-6 py-28 text-center sm:px-12 sm:py-36">
    <div className="pointer-events-none absolute left-1/2 top-1/2 h-[360px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/10 blur-[150px]" />
    <Reveal>
      <h2 className="mx-auto max-w-3xl font-['Outfit'] text-4xl font-black leading-[1.05] tracking-tighter text-white sm:text-6xl">
        Ready to{" "}
        <span className="font-['Playfair_Display'] font-normal italic text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-500">direct</span>?
      </h2>
    </Reveal>
    <Reveal delay={0.1}>
      <p className="mx-auto mt-6 max-w-lg font-['DM_Sans'] text-base leading-relaxed text-zinc-400 sm:text-lg">
        Have an account? Sign in and make your next reel now. Want in? Access is invite-only — request yours and we'll set you up.
      </p>
    </Reveal>
    <Reveal delay={0.2}>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
        <GlowButton to="/login" testid="cta-start-button">Sign in to Studio</GlowButton>
        <a href={MAILTO} data-testid="contact-email-button"
          className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 font-['DM_Sans'] text-[15px] font-medium text-zinc-200 backdrop-blur-xl transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-white/30">
          <Mail className="h-4 w-4" /> Request access
        </a>
      </div>
    </Reveal>
  </section>
);

const Footer = () => (
  <footer className="border-t border-white/10 bg-[#050505] px-6 py-16 sm:px-12">
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 sm:flex-row">
      <a href="#top" className="font-['Outfit'] text-lg font-black tracking-tight text-white">
        Listing<span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">to</span>Video
      </a>
      <div className="flex items-center gap-8 font-['DM_Sans'] text-sm text-zinc-500">
        <a href={MAILTO} data-testid="footer-email-link" className="transition-colors hover:text-amber-400">{EMAIL}</a>
        <Link to="/login" data-testid="footer-studio-link" className="transition-colors hover:text-white">Studio</Link>
        <span className="text-zinc-600">© {new Date().getFullYear()}</span>
      </div>
    </div>
  </footer>
);

/* ---------- page ---------- */
export default function Landing() {
  const [reels, setReels] = useState([]);
  useEffect(() => {
    fetch(`${BACKEND}/api/public/showcase`)
      .then((r) => (r.ok ? r.json() : { reels: [] }))
      .then((d) => setReels(d.reels || []))
      .catch(() => {});
  }, []);
  return (
    <div id="top" className="min-h-screen bg-[#050505] font-['DM_Sans'] text-white antialiased">
      <Noise />
      <Nav />
      <Hero />
      <Showcase reels={reels} />
      <HowItWorks />
      <Features />
      <MoreTools />
      <WhoFor />
      <CTA />
      <Footer />
    </div>
  );
}
